import type { BreakdownSelectionPayload } from "@/features/app-studio/api/appBreakdownsApi";
import type { AppChartDataItem, AppChartSummary } from "@/features/app-studio/api/appChartsApi";
import { isAppChartDataItem } from "@/features/app-studio/api/appChartsApi";
import type {
  AppKpiDataItem,
  AppKpiDisplayConfig,
  AppKpiDrilldownItemType,
  AppKpiFormat,
  AppKpiSummary,
} from "@/features/app-studio/api/appKpisApi";
import { isAppKpiDataItem } from "@/features/app-studio/api/appKpisApi";
import {
  isAppNuggetFeedDataItem,
  normalizeAppNuggetFeedDataItem,
  type AppNuggetFeedDataItem,
} from "@/features/app-studio/api/appNuggetFeedsApi";
import type {
  AppQueryComparisonConfig,
  AppQuerySummary,
} from "@/features/app-studio/api/appQueriesApi";
import type { DrilldownFilterValue } from "@/features/app-studio/model/appDrilldown";
import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type { AppS3SourceConfig } from "@/features/app-studio/api/appS3SourcesApi";
import type {
  AppTableApprovalConfig,
  AppTableDataItem,
  AppTableDisplayConfig,
  AppTableSummary,
} from "@/features/app-studio/api/appTablesApi";
import { isAppTableDataItem } from "@/features/app-studio/api/appTablesApi";
import {
  isAppTextWidgetDataItem,
  type AppTextWidgetDataItem,
} from "@/features/app-studio/api/appTextWidgetsApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import type { AppTabFilterRequest } from "@/features/app-studio/model/appTabFilters";
import type { ChartType, GeneralizedChartDefinition } from "@/features/chat/model/chatTypes";
import {
  readTablePageResponse,
  tableBlockSearchParams,
  type TableBlockRequest,
} from "@/features/published-apps/lib/singleTablePageStream";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppItemType = "chart" | "table" | "kpi" | "nugget_feed" | "text";

export type AppItemReorderEntry = {
  id: number;
  item_type: AppItemType;
  tab_id: number | null;
};

export async function reorderAppItems(
  appId: number,
  items: AppItemReorderEntry[],
): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/items/reorder`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

/**
 * One tab's stored AI summary. `null` when it has not been generated yet.
 *
 * Named `AiSummary` because `AppTabSummary` in `appTabsApi` is the tab's *config* DTO — the two are
 * unrelated, and the App Studio panel imports both.
 */
export type AppTabAiSummary = {
  tab_id: number;
  summary: string | null;
};

export type AppItemsData = {
  charts: AppChartDataItem[];
  tables: AppTableDataItem[];
  kpis: AppKpiDataItem[];
  nugget_feeds: AppNuggetFeedDataItem[];
  text_widgets: AppTextWidgetDataItem[];
  /** Stored per-tab AI summaries. Read from storage — this endpoint never generates. */
  tab_summaries: AppTabAiSummary[];
  app_summary: string | null;
  /**
   * True when a `POST /summaries/refresh` would produce different text, because the widgets' data no
   * longer matches what the stored summaries were written from. Always false on a filtered refresh,
   * where the executed rows are a different dataset from the one the summaries describe.
   */
  summaries_stale: boolean;
};

function parseTabSummaries(value: unknown): AppTabAiSummary[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const summaries: AppTabAiSummary[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const row = raw as { tab_id?: unknown; summary?: unknown };
    if (typeof row.tab_id === "number") {
      summaries.push({
        tab_id: row.tab_id,
        summary: typeof row.summary === "string" ? row.summary : null,
      });
    }
  }
  return summaries;
}

export function parseAppItemsData(data: unknown): AppItemsData {
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const body = data as {
    charts?: unknown;
    tables?: unknown;
    kpis?: unknown;
    nugget_feeds?: unknown;
    text_widgets?: unknown;
    tab_summaries?: unknown;
    app_summary?: unknown;
    summaries_stale?: unknown;
  };
  const charts = body.charts;
  const tables = body.tables;
  const kpis = body.kpis;
  const nuggetFeeds = body.nugget_feeds ?? [];
  const textWidgets = body.text_widgets ?? [];
  if (
    !Array.isArray(charts) ||
    !charts.every(isAppChartDataItem) ||
    !Array.isArray(tables) ||
    !tables.every(isAppTableDataItem) ||
    !Array.isArray(kpis) ||
    !kpis.every(isAppKpiDataItem) ||
    !Array.isArray(nuggetFeeds) ||
    !nuggetFeeds.every(isAppNuggetFeedDataItem) ||
    !Array.isArray(textWidgets) ||
    !textWidgets.every(isAppTextWidgetDataItem)
  ) {
    throw new Error("Unexpected response shape");
  }
  return {
    charts,
    tables,
    kpis,
    nugget_feeds: nuggetFeeds.map(normalizeAppNuggetFeedDataItem),
    text_widgets: textWidgets,
    // Tolerant like `nugget_feeds` above: the summary fields are additive, so a response without
    // them is still valid.
    tab_summaries: parseTabSummaries(body.tab_summaries),
    app_summary: typeof body.app_summary === "string" ? body.app_summary : null,
    summaries_stale: body.summaries_stale === true,
  };
}

/**
 * One page of a builder table widget's rows, as a page-scoped SSE response.
 *
 * The unified builder refresh is a stream (see `useAppBuilderItemsStream`), so there is no
 * plain-JSON `fetchAppItemsData` any more. A page turn re-requests that same endpoint scoped to
 * one widget: HTTP SSE cannot push into an already-drained response, so navigating pages is a new
 * request that returns just this table's updated item frame.
 *
 * `cursor` is the opaque `next_cursor` the client was handed for the page being navigated *to*,
 * or null for page 1. The client never needs to know whether the server paged by OFFSET or by
 * keyset — that is a read-time decision made from the widget's cached row count.
 */
export async function fetchAppTableBlock(
  appId: number,
  params: TableBlockRequest,
): Promise<AppTableDataItem> {
  const query = tableBlockSearchParams(params);
  const res = await apiFetch(`/v1/apps/${appId}/items/data?${query.toString()}`, {
    method: "GET",
    headers: { Accept: "text/event-stream" },
  });
  return readTablePageResponse(res);
}

/**
 * What a scoped re-run sends: the tab, its filters, and optionally which widgets and under which
 * breakdown options. Shared by the builder, published and share-link refreshes so the three bodies
 * cannot drift apart.
 */
export type AppItemsDataRequestParams = {
  tabId: number | null;
  filters: AppTabFilterRequest[];
  /** Narrow the run to these widgets — a breakdown switch re-runs only the ones it re-cuts. */
  items?: AppWidgetRef[];
  /** Breakdown id → option key. Omitted breakdowns run their default option. */
  breakdowns?: BreakdownSelectionPayload;
};

/** The JSON body of `POST …/items/data`, leaving out what the caller did not ask for. */
export function itemsDataRequestBody(params: AppItemsDataRequestParams): string {
  return JSON.stringify({
    tab_id: params.tabId,
    filters: params.filters,
    ...(params.items !== undefined ? { items: params.items } : {}),
    ...(params.breakdowns !== undefined ? { breakdowns: params.breakdowns } : {}),
  });
}

/**
 * Filtered refresh for one tab: executes only that tab's widgets with the given ephemeral tab
 * filters ANDed into each query. `tabId` null runs every widget (an app with no tabs). Results
 * are not persisted or cached; each widget carries `ignored_filters` for filters that did not
 * apply to it.
 */
export async function fetchAppItemsDataFiltered(
  appId: number,
  params: AppItemsDataRequestParams,
): Promise<AppItemsData> {
  const res = await apiFetch(`/v1/apps/${appId}/items/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: itemsDataRequestBody(params),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseAppItemsData(await res.json());
}

/** One query-backed widget, addressed the way the reorder and summary payloads address one. */
export type AppWidgetRef = {
  id: number;
  item_type: "chart" | "table" | "kpi";
};

/**
 * Re-run **only** the named widgets, leaving every other query in the app alone.
 *
 * The builder's save path. Adding or editing one widget used to re-open the whole items stream,
 * which re-ran every query in the app and put every card back into its skeleton — so the widget
 * you had just saved appeared no sooner than the slowest unrelated one. Naming the widgets makes
 * the cost proportional to the edit.
 *
 * `refs` may be empty, and that is a real request: the query-less kinds (Nugget Feeds, text
 * widgets) come back in full either way, which is how a newly created one — whose id the caller
 * does not have — reaches the grid. `tab_summaries` and `app_summary` are read from storage, and
 * `summaries_stale` is always false here (a subset cannot judge the whole app's summaries); the
 * caller posts to `/summaries/refresh` when it knows it changed something summarisable.
 */
export async function fetchAppItemsDataForWidgets(
  appId: number,
  refs: AppWidgetRef[],
  breakdowns?: BreakdownSelectionPayload,
): Promise<AppItemsData> {
  const res = await apiFetch(`/v1/apps/${appId}/items/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: itemsDataRequestBody({ tabId: null, filters: [], items: refs, breakdowns }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseAppItemsData(await res.json());
}

/**
 * The statement one widget sends the warehouse, built under the tab's filters but never run.
 *
 * `sql` is what really executes. When the domain check refuses it, `sql` is the filtered statement
 * it refused and `rejection` says why; `null` with no rejection means the widget reads files.
 */
export type AppWidgetExecutedSql = {
  sql: string | null;
  rejection: string | null;
  /** A stored filter template built part of the statement. */
  template_used: boolean;
  /** Filters that did not apply — the grid's "Not filtered by". */
  ignored_filters: string[];
};

/**
 * Super Administrators only: the final SQL of one widget, filter templates and all.
 *
 * `filters` is what the tab currently has applied, as the grid sent it. `null` means nothing has
 * been applied on the grid, so the server uses the tab's saved defaults — what the grid shows then.
 */
export async function fetchAppWidgetExecutedSql(
  appId: number,
  ref: AppWidgetRef,
  filters: AppTabFilterRequest[] | null,
  breakdowns?: BreakdownSelectionPayload,
): Promise<AppWidgetExecutedSql> {
  const res = await apiFetch(
    `/v1/apps/${appId}/items/${ref.item_type}/${ref.id}/executed-sql`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...(filters === null ? {} : { filters }),
        ...(breakdowns !== undefined ? { breakdowns } : {}),
      }),
    },
  );
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return (await res.json()) as AppWidgetExecutedSql;
}

/** Which widget opens the drill-down, and what was clicked. */
export type DrilldownRequest = {
  ownerType: "kpi" | "table";
  ownerId: number;
  /** The clicked column. Omitted for a KPI owner, which has no column context. */
  triggerColumn?: string | null;
  /**
   * The clicked row's value for each of the drill-down's configured filter columns, ANDed by the
   * server. A `null` value filters the target to its own null rows. Omitted for a KPI owner.
   */
  filterValues?: DrilldownFilterValue[] | null;
  tabId: number | null;
  filters: AppTabFilterRequest[];
  /** The tab's breakdown picks, so the target is cut the way the grid behind it is. */
  breakdowns?: BreakdownSelectionPayload;
};

export type DrilldownData = {
  item_type: "chart" | "table";
  item: AppChartDataItem | AppTableDataItem;
  /**
   * Rows of the widget the dialog was opened *from*, under the same predicates. Present only on
   * the published viewer's path, and only when the target can be matched to them at all — every
   * other caller gets `undefined` and behaves as it did before this existed.
   *
   * They are what lets the dialog approve rows in bulk: an approval belongs to the owner widget,
   * and the client can only submit a row it holds, which without this is one block's worth.
   */
  owner_rows?: Record<string, unknown>[];
};

/** Rows arrive as an untyped array; anything that is not a row object is dropped rather than cast. */
function parseOwnerRows(value: unknown): Record<string, unknown>[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter(
    (row): row is Record<string, unknown> =>
      typeof row === "object" && row !== null && !Array.isArray(row),
  );
}

export function parseDrilldownData(payload: unknown): DrilldownData {
  if (typeof payload !== "object" || payload === null) {
    throw new Error("Unexpected response shape");
  }
  const row = payload as Record<string, unknown>;
  const itemType = row.item_type;
  const ownerRows = parseOwnerRows(row.owner_rows);
  if (itemType === "chart" && isAppChartDataItem(row.item)) {
    return { item_type: "chart", item: row.item, owner_rows: ownerRows };
  }
  if (itemType === "table" && isAppTableDataItem(row.item)) {
    return { item_type: "table", item: row.item, owner_rows: ownerRows };
  }
  throw new Error("Unexpected response shape");
}

export function drilldownBody(request: DrilldownRequest): string {
  return JSON.stringify({
    owner_type: request.ownerType,
    owner_id: request.ownerId,
    trigger_column: request.triggerColumn ?? null,
    filter_values: request.filterValues ?? null,
    tab_id: request.tabId,
    filters: request.filters,
    ...(request.breakdowns !== undefined ? { breakdowns: request.breakdowns } : {}),
  });
}

/**
 * Execute one drill-down widget, filtered by the clicked cell.
 *
 * Separate from `fetchAppItemsDataFiltered` because that applies its filters to every widget on
 * the tab, whereas the cell's predicate must reach only the target. The tab's own active filters
 * are passed along so the dialog agrees with the tab it was opened from.
 */
export async function fetchAppDrilldownData(
  appId: number,
  request: DrilldownRequest,
): Promise<DrilldownData> {
  const res = await apiFetch(`/v1/apps/${appId}/items/drilldown/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: drilldownBody(request),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseDrilldownData(await res.json());
}

/** Published-viewer twin of `fetchAppDrilldownData`. */
export async function fetchPublishedAppDrilldownData(
  appId: number,
  request: DrilldownRequest,
): Promise<DrilldownData> {
  const res = await apiFetch(`/v1/published-apps/${appId}/items/drilldown/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: drilldownBody(request),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseDrilldownData(await res.json());
}

export type BatchQuerySpec = {
  query_id?: number | null;
  name?: string;
  /**
   * Absent for `input_mode: "s3"` and `"comparison"`, whose source is `input_config`: a file
   * selection, or the comparison (which the server also names, so `name` is not needed).
   */
  sql?: string;
  input_mode?: AppWidgetInputMode;
  input_config?:
    | AppQueryBuilderCompileRequest
    | AppS3SourceConfig
    | AppQueryComparisonConfig
    | null;
};

export type BatchWidgetSpec = {
  item_type: AppItemType;
  column_span?: number | null;
  tab_id?: number | null;
  // chart and table: false hides the widget's AI summary (it is still generated)
  show_summary?: boolean;
  // chart
  chart_definition?: GeneralizedChartDefinition;
  preferred_chart_type?: ChartType | null;
  // table (its title) and kpi (the info tooltip under its name)
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  /** Tables only: the API rejects this on any other widget kind. */
  approval_enabled?: boolean;
  /** Tables only, and only alongside `approval_enabled`; the API rejects it otherwise. */
  approval_config?: AppTableApprovalConfig | null;
  /** Tables only: the column the widget's rows are paged by. Rejected on other widget kinds. */
  // kpi
  name?: string;
  value_column?: string | null;
  format?: AppKpiFormat;
  /** The card's look. Its own key because `display_config` above is a table's. */
  kpi_display_config?: AppKpiDisplayConfig | null;
  /**
   * Drill-down target for a KPI: a chart/table that already exists on the same tab. Widgets
   * created by this same batch have no ids yet, so they cannot be targeted here.
   */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
};

export type CreateWidgetsBatchPayload = {
  query: BatchQuerySpec;
  widgets: BatchWidgetSpec[];
};

export type CreateWidgetsBatchResponse = {
  query: AppQuerySummary;
  charts: AppChartSummary[];
  tables: AppTableSummary[];
  kpis: AppKpiSummary[];
};

export async function createWidgetsBatch(
  appId: number,
  payload: CreateWidgetsBatchPayload,
): Promise<CreateWidgetsBatchResponse> {
  const res = await apiFetch(`/v1/apps/${appId}/widgets/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  return data as CreateWidgetsBatchResponse;
}
